// /client/src/components/DeploymentView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';

const DeploymentView = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  // Deployment data
  const [deployment, setDeployment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Message queue data
  const [queueMessages, setQueueMessages] = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueStats, setQueueStats] = useState({ pending: 0, queuedToTwilio: 0, sent: 0, failed: 0, cancelled: 0, total: 0 });

  // Pods data
  const [podsData, setPodsData] = useState([]);
  const [podsLoading, setPodsLoading] = useState(true);

  // Replies data
  const [replies, setReplies] = useState([]);
  const [repliesLoading, setRepliesLoading] = useState(true);

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  // Force complete state
  const [showForceCompleteModal, setShowForceCompleteModal] = useState(false);
  const [forceCompleteMode, setForceCompleteMode] = useState('deployment');
  const [selectedPodsForCancel, setSelectedPodsForCancel] = useState([]);
  const [forceCompleteReason, setForceCompleteReason] = useState('');
  const [forceCompleting, setForceCompleting] = useState(false);
  const [forceCompleteError, setForceCompleteError] = useState('');

  // Fetch deployment details
  const fetchDeployment = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/deployments/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch deployment');

      const data = await response.json();
      setDeployment(data.deployment);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch message queue for this deployment
  const fetchQueueMessages = useCallback(async () => {
    try {
      setQueueLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/deployments/${id}/queue`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch queue');

      const data = await response.json();
      setQueueMessages(data.messages || []);
      setQueueStats(data.stats || { pending: 0, queuedToTwilio: 0, sent: 0, failed: 0, cancelled: 0, total: 0 });
    } catch (err) {
      console.error('Queue fetch error:', err);
    } finally {
      setQueueLoading(false);
    }
  }, [id]);

  // Fetch pods with members
  const fetchPodsData = useCallback(async () => {
    try {
      setPodsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/deployments/${id}/pods`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch pods');

      const data = await response.json();
      setPodsData(data.pods || []);
    } catch (err) {
      console.error('Pods fetch error:', err);
    } finally {
      setPodsLoading(false);
    }
  }, [id]);

  // Fetch replies/responses
  const fetchReplies = useCallback(async () => {
    try {
      setRepliesLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/deployments/${id}/replies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch replies');

      const data = await response.json();
      setReplies(data.replies || []);
    } catch (err) {
      console.error('Replies fetch error:', err);
    } finally {
      setRepliesLoading(false);
    }
  }, [id]);

  // Sync with Twilio
  const syncWithTwilio = async () => {
    try {
      setSyncing(true);
      setSyncMessage('');
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/deployments/${id}/sync`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSyncMessage(`${data.message}`);
        // Refresh all data
        await fetchDeployment();
        await fetchQueueMessages();
      } else {
        setSyncMessage(`✗ ${data.error || 'Sync failed'}`);
      }
    } catch (err) {
      setSyncMessage(`✗ ${err.message}`);
    } finally {
      setSyncing(false);
      // Clear message after 5 seconds
      setTimeout(() => setSyncMessage(''), 5000);
    }
  };

  // Toggle pod selection for per-pod force complete
  const togglePodForCancel = (podId) => {
    setSelectedPodsForCancel(prev => {
      if (prev.includes(podId)) {
        return prev.filter(id => id !== podId);
      }
      return [...prev, podId];
    });
  };

  // Get pod status from deployment data
  const getPodDeploymentStatus = (podId) => {
    const podRef = deployment?.pods?.find(p => {
      const refId = p.podId?._id || p.podId;
      return refId?.toString() === podId.toString();
    });
    return podRef?.status || 'unknown';
  };

  // Handle force complete submission
  const handleForceComplete = async () => {
    try {
      setForceCompleting(true);
      setForceCompleteError('');
      const token = localStorage.getItem('token');

      if (forceCompleteMode === 'deployment') {
        // Force complete entire deployment
        const response = await fetch(`/api/deployments/${id}/force-complete`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reason: forceCompleteReason.trim() || 'Force-completed by admin'
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to force-complete deployment');
        }

        setSyncMessage(`Deployment force-completed. ${data.stats.totalCancelled} messages cancelled.`);
        setShowForceCompleteModal(false);
        
        // Refresh all data
        await fetchDeployment();
        await fetchQueueMessages();
        await fetchPodsData();

      } else {
        // Force complete selected pods
        if (selectedPodsForCancel.length === 0) {
          setForceCompleteError('Please select at least one pod.');
          return;
        }

        let totalCancelled = 0;
        let podsCompleted = 0;
        let lastDeploymentAutoCompleted = false;

        for (const podId of selectedPodsForCancel) {
          const response = await fetch(`/api/deployments/${id}/pods/${podId}/force-complete`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              reason: forceCompleteReason.trim() || 'Pod force-completed by admin'
            })
          });

          const data = await response.json();

          if (response.ok) {
            totalCancelled += data.stats.totalCancelled;
            podsCompleted++;
            if (data.deploymentAutoCompleted) {
              lastDeploymentAutoCompleted = true;
            }
          } else {
            console.error(`Failed to force-complete pod ${podId}:`, data.error);
          }
        }

        let message = `${podsCompleted} pod${podsCompleted !== 1 ? 's' : ''} force-completed. ${totalCancelled} messages cancelled.`;
        if (lastDeploymentAutoCompleted) {
          message += ' Deployment auto-completed.';
        }

        setSyncMessage(message);
        setShowForceCompleteModal(false);
        setSelectedPodsForCancel([]);
        
        // Refresh all data
        await fetchDeployment();
        await fetchQueueMessages();
        await fetchPodsData();
      }

    } catch (err) {
      setForceCompleteError(err.message);
    } finally {
      setForceCompleting(false);
    }
  };

  // Reset modal state when closing
  const closeForceCompleteModal = () => {
    setShowForceCompleteModal(false);
    setForceCompleteMode('deployment');
    setSelectedPodsForCancel([]);
    setForceCompleteReason('');
    setForceCompleteError('');
  };

  // Initial load with auto-sync
  useEffect(() => {
    const initializeData = async () => {
      await fetchDeployment();
      await fetchPodsData();
      
      // Auto-sync with Twilio
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`/api/deployments/${id}/sync`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        if (data.success) {
          setSyncMessage(`${data.message}`);
          setTimeout(() => setSyncMessage(''), 5000);
        }
      } catch (err) {
        console.error('Auto-sync failed:', err);
      }
      
      await fetchQueueMessages();
      await fetchReplies();
    };
    
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-refresh every 30 seconds for active deployments (with sync)
  useEffect(() => {
    if (deployment?.status === 'scheduled' || deployment?.status === 'in delivery') {
      const interval = setInterval(async () => {
        const token = localStorage.getItem('token');
        try {
          await fetch(`/api/deployments/${id}/sync`, {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        } catch (err) {
          console.error('Auto-sync failed:', err);
        }
        fetchQueueMessages();
        fetchReplies();
      }, 30000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deployment?.status, id]);

  const getStatusBadge = (status) => {
    const badges = {
      'draft': 'is-light',
      'scheduled': 'is-info',
      'in delivery': 'is-warning',
      'completed': 'is-success',
      'cancelled': 'is-danger',
      'pending': 'is-light',
      'queued-to-twilio': 'is-info',
      'sent': 'is-success',
      'failed': 'is-danger'
    };
    return badges[status] || 'is-light';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          <Navigation />
          <div className='mt-2 pt-6 container px-6'>
            <div className="has-text-centered py-6">
              <p>Loading deployment...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !deployment) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          <Navigation />
          <div className='mt-2 pt-6 container px-6'>
            <div className="notification is-danger">
              {error || 'Deployment not found'}
            </div>
            <button 
              className="button is-small is-text mb-4"
              onClick={() => navigate('/deployments')}
            >
              ← Back
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          <Navigation />

          <div className='mt-2 pt-0 container px-6'>
            {/* Header */}
            <div className="is-flex is-justify-content-space-between is-align-items-center mb-4">
              <div className='mt-2 pt-6 container px-6'>
                <button 
                  className="button is-small is-text mb-4"
                  onClick={() => navigate('/deployments')}
                >
                  ← Back
                </button>
                <h1 className='h1 my-2'>{deployment.name}</h1>
                <p className="has-text-grey">
                  {deployment.programId?.programName} • {deployment.productId?.name}
                </p>
              </div>
              <div className="is-flex is-align-items-center" style={{ gap: '10px' }}>
                <span className={`tag is-medium ${getStatusBadge(deployment.status)}`}>
                  {deployment.status === 'in delivery' ? 'In Delivery' : 
                   deployment.status?.charAt(0).toUpperCase() + deployment.status?.slice(1)}
                </span>
                {deployment.status === 'draft' && (
                  <button 
                    className="button is-primary"
                    onClick={() => navigate(`/deployments/${id}/edit`)}
                  >
                    Edit
                  </button>
                )}
                {['scheduled', 'in delivery'].includes(deployment.status) && (
                  <button 
                    className="button is-danger is-outlined"
                    onClick={() => setShowForceCompleteModal(true)}
                  >
                    Force Complete
                  </button>
                )}
              </div>
            </div>

            {/* Sync Message */}
            {syncMessage && (
              <div className={`notification ${syncMessage.startsWith('✗') ? 'is-danger' : 'is-success'} is-light py-2 mb-4`}>
                {syncMessage}
              </div>
            )}

            {/* Three Column Layout */}
            <div className="columns">
              {/* Left Column - Message Queue/History */}
              <div className="column is-one-third">
                <div className="box" style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
                  <div className="is-flex is-justify-content-space-between is-align-items-center mb-3">
                    <h2 className="subtitle is-5 mb-0 is-bold">
                      {queueStats.pending === 0 && queueStats.queuedToTwilio === 0 && queueStats.total > 0 
                        ? 'Message History' 
                        : 'Message Queue'}
                    </h2>
                    <div className="buttons are-small mb-0">
                      <button 
                        className={`button is-small ${syncing ? 'is-loading' : ''}`}
                        onClick={syncWithTwilio}
                        disabled={syncing || queueLoading}
                        title="Sync statuses from Twilio"
                      >
                        Sync
                      </button>
                    </div>
                  </div>

                  {/* Stats Summary */}
                  <div className="is-flex mb-3" style={{ gap: '8px', flexWrap: 'wrap' }}>
                    {queueStats.pending > 0 && (
                      <span className="tag is-light">
                        Pending: {queueStats.pending}
                      </span>
                    )}
                    {queueStats.queuedToTwilio > 0 && (
                      <span className="tag is-info is-light">
                        Queued: {queueStats.queuedToTwilio}
                      </span>
                    )}
                    <span className="tag is-success is-light">
                      Sent: {queueStats.sent}
                    </span>
                    {queueStats.failed > 0 && (
                      <span className="tag is-danger is-light">
                        Failed: {queueStats.failed}
                      </span>
                    )}
                    <span className="tag is-dark is-light">
                      Total: {queueStats.total}
                    </span>
                  </div>

                  {queueLoading ? (
                    <p className="has-text-centered has-text-grey">Loading...</p>
                  ) : queueMessages.length === 0 ? (
                    <p className="has-text-centered has-text-grey is-size-7">
                      No messages queued yet
                    </p>
                  ) : (
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                      <table className="table is-fullwidth is-narrow is-size-7">
                        <thead>
                          <tr>
                            <th>Recipient</th>
                            <th>#</th>
                            <th>Scheduled</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {queueMessages.slice(0, 100).map((msg, idx) => (
                            <tr key={msg._id || idx}>
                              <td>
                                <span title={msg.podName}>
                                  {msg.recipientName}
                                </span>
                              </td>
                              <td>{msg.messageLabel?.replace(/^(Message |Day )/i, '') || '-'}</td>
                              <td>{formatDate(msg.scheduledFor)}</td>
                              <td>
                                <span 
                                  className={`tag is-small ${getStatusBadge(msg.status)}`}
                                  title={msg.errorMessage || ''}
                                >
                                  {msg.status === 'queued-to-twilio' ? 'queued' : msg.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {queueMessages.length > 100 && (
                        <p className="has-text-centered is-size-7 has-text-grey">
                          Showing 100 of {queueMessages.length}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Center Column - Pods & Recipients */}
              <div className="column is-one-third">
                <div className="box" style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
                  <div className="is-flex is-justify-content-space-between is-align-items-center mb-3">
                    <h2 className="subtitle is-5 mb-0 is-bold">Pods</h2>
                    <span className="tag is-light">
                      {podsData.length} pod{podsData.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {podsLoading ? (
                    <p className="has-text-centered has-text-grey">Loading...</p>
                  ) : podsData.length === 0 ? (
                    <p className="has-text-centered has-text-grey is-size-7">
                      No pods assigned
                    </p>
                  ) : (
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                      {podsData.map((pod) => {
                        const podStatus = getPodDeploymentStatus(pod._id);
                        return (
                          <div 
                            key={pod._id} 
                            className="mb-3 p-3"
                            style={{ 
                              backgroundColor: '#f5f5f5', 
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                            onClick={() => navigate(`/deployments/${id}/pods/${pod._id}`)}
                          >
                            <div className="is-flex is-justify-content-space-between is-align-items-center">
                              <div>
                                <strong className="colorBlue">
                                  {pod.podName}
                                </strong>
                                {['completed', 'cancelled'].includes(podStatus) && (
                                  <span className={`tag is-small ml-2 ${podStatus === 'completed' ? 'is-success' : 'is-danger'}`}>
                                    {podStatus}
                                  </span>
                                )}
                              </div>
                              <span className="is-size-7 has-text-grey">
                                {pod.memberCount} member{pod.memberCount !== 1 ? 's' : ''} →
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Replies */}
              <div className="column is-one-third">
                <div className="box" style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
                  <div className="is-flex is-justify-content-space-between is-align-items-center mb-3">
                    <h2 className="subtitle is-5 mb-0 is-bold">Replies</h2>
                    <button 
                      className="button is-small is-light"
                      onClick={fetchReplies}
                      disabled={repliesLoading}
                    >
                      Reload
                    </button>
                  </div>

                  {repliesLoading ? (
                    <p className="has-text-centered has-text-grey">Loading...</p>
                  ) : replies.length === 0 ? (
                    <p className="has-text-centered has-text-grey is-size-7">
                      No replies yet
                    </p>
                  ) : (
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                      {replies.map((reply, idx) => (
                        <div 
                          key={reply._id || idx} 
                          className="mb-3 p-2" 
                          style={{ backgroundColor: '#f5f5f5', borderRadius: '6px', cursor: 'pointer' }}
                          onClick={() => reply.podId && navigate(`/deployments/${id}/pods/${reply.podId}`)}
                        >
                          <div className="is-flex is-justify-content-space-between is-size-7 mb-1">
                            <span className='colorBlack'>{reply.senderName || 'Unknown'}</span>
                            <span className="has-text-grey">{formatDate(reply.receivedAt)}</span>
                          </div>
                          
                          {/* Message body with clickable links */}
                          <p className="is-size-7" style={{ wordBreak: 'break-word' }}>
                            {reply.body?.split(/(https?:\/\/[^\s]+)/g).map((part, i) => 
                              part.match(/^https?:\/\//) ? (
                                <a 
                                  key={i}
                                  href={part} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {part}
                                </a>
                              ) : part
                            )}
                          </p>
                          
                          {/* Media preview */}
                          {reply.mediaUrl && reply.mediaUrl.startsWith('http') && (
                            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                              {reply.mediaUrl.match(/\.(mp4|mov|3gp|webm)$/i) ? (
                                <video 
                                  controls 
                                  style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: '4px' }}
                                >
                                  <source src={reply.mediaUrl} />
                                </video>
                              ) : (
                                <a href={reply.mediaUrl} target="_blank" rel="noopener noreferrer">
                                  <img 
                                    src={reply.mediaUrl} 
                                    alt="Media" 
                                    style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: '4px' }}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.outerHTML = `<a href="${reply.mediaUrl}" target="_blank" class="is-size-7">📎 View attachment</a>`;
                                    }}
                                  />
                                </a>
                              )}
                            </div>
                          )}
                          
                          <p className="is-size-7 has-text-grey mt-1">
                            Pod: {reply.podName || 'Unknown'} →
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Force Complete Modal */}
      {showForceCompleteModal && (
        <div className="modal is-active">
          <div className="modal-background" onClick={closeForceCompleteModal}></div>
          <div className="modal-card" style={{ maxWidth: '600px' }}>
            <header className="modal-card-head has-background-danger-light">
              <h2 className="modal-card-title">
                Force Complete
              </h2>
              <button 
                className="delete" 
                aria-label="close"
                onClick={closeForceCompleteModal}
              ></button>
            </header>
            <section className="modal-card-body">
              <div className="content">
                
                {/* Mode Selection */}
                <div className="field">
                  <label className="label">What would you like to force-complete?</label>
                  <div className="control">
                    <label className="radio mr-4">
                      <input 
                        type="radio" 
                        name="forceCompleteMode" 
                        value="deployment"
                        checked={forceCompleteMode === 'deployment'}
                        onChange={() => {
                          setForceCompleteMode('deployment');
                          setSelectedPodsForCancel([]);
                        }}
                        className="mr-2"
                      />
                      Entire Deployment
                    </label>
                    <label className="radio">
                      <input 
                        type="radio" 
                        name="forceCompleteMode" 
                        value="pods"
                        checked={forceCompleteMode === 'pods'}
                        onChange={() => setForceCompleteMode('pods')}
                        className="mr-2"
                      />
                      Specific Pod(s)
                    </label>
                  </div>
                </div>

                {/* Full Deployment Warning */}
                {forceCompleteMode === 'deployment' && (
                  <div className="notification is-danger is-light mt-4">
                    <p className="mb-2"><strong>This will:</strong></p>
                    <ul className="mt-0 mb-0">
                      <li>Cancel all pending and queued messages</li>
                      <li>Cancel messages already scheduled at Twilio</li>
                      <li>Deactivate relay for all pods</li>
                      <li>Mark the deployment as <strong>cancelled</strong></li>
                    </ul>
                    <p className="mt-2 mb-0"><strong>Messages already delivered will not be affected.</strong></p>
                  </div>
                )}

                {/* Pod Selection */}
                {forceCompleteMode === 'pods' && (
                  <div className="mt-4">
                    <label className="label">Select pod(s) to force-complete:</label>
                    <div className="box has-background-light" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {podsData.length === 0 ? (
                        <p className="has-text-grey is-size-7">No pods found</p>
                      ) : (
                        podsData.map((pod) => {
                          const podStatus = getPodDeploymentStatus(pod._id);
                          const isTerminal = ['completed', 'cancelled'].includes(podStatus);
                          const isSelected = selectedPodsForCancel.includes(pod._id);

                          return (
                            <label 
                              key={pod._id} 
                              className={`is-flex is-align-items-center p-2 mb-2 ${isTerminal ? '' : 'is-clickable'}`}
                              style={{ 
                                backgroundColor: isSelected ? '#fff3f3' : 'white',
                                borderRadius: '4px',
                                opacity: isTerminal ? 0.5 : 1,
                                cursor: isTerminal ? 'not-allowed' : 'pointer'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isTerminal}
                                onChange={() => togglePodForCancel(pod._id)}
                                className="mr-3"
                              />
                              <div className="is-flex-grow-1">
                                <span className="colorBlack">{pod.podName}</span>
                                <span className="is-size-7 has-text-grey ml-2">
                                  ({pod.memberCount} member{pod.memberCount !== 1 ? 's' : ''})
                                </span>
                              </div>
                              <span className={`tag is-small ${
                                podStatus === 'completed' ? 'is-success' :
                                podStatus === 'cancelled' ? 'is-danger' :
                                podStatus === 'active' ? 'is-warning' :
                                'is-light'
                              }`}>
                                {podStatus}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>

                    {selectedPodsForCancel.length > 0 && (
                      <div className="notification is-danger is-light mt-3">
                        <p className="mb-2"><strong>This will:</strong></p>
                        <ul className="mt-0 mb-0">
                          <li>Cancel pending and queued messages for {selectedPodsForCancel.length} pod{selectedPodsForCancel.length !== 1 ? 's' : ''}</li>
                          <li>Cancel messages already scheduled at Twilio for {selectedPodsForCancel.length === 1 ? 'this pod' : 'these pods'}</li>
                          <li>Deactivate relay for {selectedPodsForCancel.length === 1 ? 'this pod' : 'these pods'}</li>
                          <li>If all pods finish, the deployment will auto-complete</li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Reason Field */}
                <div className="field mt-4">
                  <label className="label">Reason (Optional)</label>
                  <div className="control">
                    <textarea
                      className="textarea"
                      placeholder="Enter reason for force-completing..."
                      value={forceCompleteReason}
                      onChange={(e) => setForceCompleteReason(e.target.value)}
                      rows={2}
                    />
                  </div>
                </div>

                {/* Error Display */}
                {forceCompleteError && (
                  <div className="notification is-danger mt-3">
                    {forceCompleteError}
                  </div>
                )}
              </div>
            </section>
            <footer className="modal-card-foot">
              <button 
                className={`button is-danger mx-1 ${forceCompleting ? 'is-loading' : ''}`}
                onClick={handleForceComplete}
                disabled={forceCompleting || (forceCompleteMode === 'pods' && selectedPodsForCancel.length === 0)}
              >
                {forceCompleting 
                  ? 'Processing...' 
                  : forceCompleteMode === 'deployment'
                    ? 'Force Complete Deployment'
                    : `Force Complete ${selectedPodsForCancel.length} Pod${selectedPodsForCancel.length !== 1 ? 's' : ''}`
                }
              </button>
              <button 
                className="button mx-1" 
                onClick={closeForceCompleteModal}
                disabled={forceCompleting}
              >
                Cancel
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default DeploymentView;